import { useState } from "react"
import { useTranslation } from "react-i18next"
import { FileText } from "lucide-react"
import { ExportStatusIcon } from "@/components/maps/orderDetails/ExportStatusIcon"
import { useLatestDeliveryOrder } from "@/hooks/useLatestDeliveryOrder"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import type { RouteStop } from "@/reducers/routes2gSlice"
import OrderDetailsDialog, {
  classifyExportStatus,
  exportStatusTooltip,
} from "./orderDetails/OrderDetailsDialog"

const DeliveryOrderExportStatusCard = ({
  driverJid,
  stops,
}: {
  driverJid: string
  stops: RouteStop[] | undefined
}) => {
  const { t } = useTranslation()
  const [dialogOpen, setDialogOpen] = useState(false)
  const { enabled, latest } = useLatestDeliveryOrder(
    driverJid,
    stops,
    dialogOpen,
  )

  if (!enabled) return null

  const hasOrder = Boolean(latest?.id)
  const statusClass = classifyExportStatus(latest?.exportStatus ?? "")
  const statusTooltip = exportStatusTooltip(
    hasOrder,
    statusClass,
    latest?.exportId,
  )

  return (
    <>
      <button
        type="button"
        onClick={() => hasOrder && setDialogOpen(true)}
        disabled={!hasOrder}
        className="mx-2 my-2 flex w-fit items-center gap-2 rounded-md border p-2 text-xs transition-colors enabled:cursor-pointer enabled:hover:bg-muted/50 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <span className="flex items-center gap-2">
          <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
          {t("orders.orderDetails.title")}
        </span>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex shrink-0 items-center">
                <ExportStatusIcon status={statusClass} />
              </span>
            </TooltipTrigger>
            <TooltipContent>{statusTooltip}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </button>

      {hasOrder && (
        <OrderDetailsDialog
          driverJid={driverJid}
          deliveryOrderId={latest!.id}
          isOpen={dialogOpen}
          onClose={() => setDialogOpen(false)}
        />
      )}
    </>
  )
}

export default DeliveryOrderExportStatusCard
